/*
 * THE CONSOLE'S ALERTS for what arrives live — a new guest request (`stores/requests.ts`), a new
 * complaint (`stores/feedback.ts`): a short tone from the Web Audio API (no sound file) and,
 * where the person allowed it, a browser notification. Reception is not always looking at the
 * screen.
 */

/*
 * One audio context for the console, made at the person's first click: browsers keep audio
 * silent until the page has had a gesture, and a context made later by an event would stay
 * suspended.
 */
let audio: AudioContext | null = null

if (typeof window !== 'undefined') {
    window.addEventListener('pointerdown', () => {
        try {
            audio ??= new AudioContext()
            void audio.resume()
        } catch {
            audio = null
        }
    }, { once: true })
}

/** The tone: two short notes (rising for a request, falling for a complaint). */
export const chime = (notes: number[] = [880, 1320]) => {
    const context = audio
    if (!context) return

    try {
        const now = context.currentTime

        notes.forEach((frequency, index) => {
            const oscillator = context.createOscillator()
            const gain       = context.createGain()

            oscillator.frequency.value = frequency
            gain.gain.setValueAtTime(0.0001, now + index * 0.18)
            gain.gain.exponentialRampToValueAtTime(0.25, now + index * 0.18 + 0.02)
            gain.gain.exponentialRampToValueAtTime(0.0001, now + index * 0.18 + 0.16)
            oscillator.connect(gain).connect(context.destination)
            oscillator.start(now + index * 0.18)
            oscillator.stop(now + index * 0.18 + 0.17)
        })
    } catch {
        // A browser without audio (or one that blocks it before a click): the badge still counts.
    }
}

/** A browser notification, where the person allowed them; `tag` keeps one per thing. */
export const notifyBrowser = (title: string, body: string, tag: string) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return

    try {
        new Notification(title, { body, tag })
    } catch {
        // Some browsers only show notifications from a service worker; the tone was enough.
    }
}
